import { ApiResponse, GlobalStore } from '@roc/feature-app-core';
import { InsuranceInformationService } from '../services';
import { InsuranceInformationBaseStore } from './insuranceInformationBaseStore';
import { InsuranceQuoteFormStore } from './insuranceQuoteFormStore';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { FinalDocumentType } from '../util';
import ResendQuoteStore from './resendQuoteStore';

export class InsuranceInformationStore extends InsuranceInformationBaseStore {
  insuranceInformationService: InsuranceInformationService;

  constructor(
    globalStore: GlobalStore,
  ) {
    super(globalStore);
    this.insuranceInformationService = new InsuranceInformationService();
    this.insuranceQuoteFormStore = new InsuranceQuoteFormStore(false, this, globalStore);
    this.resendElmsureQuoteStore = new ResendQuoteStore(this.globalStore, this, false);
  }

  *getSelectedQuotesByLoanId() {
    try {
      const response = yield this.insuranceInformationService.getSelectedQuotesByLoanId(this.loanId);
      this.setFinalChoiceQuotes(JSON.parse(response?.data?.data));
      this.createOptionQuotes(this.finalChoiceQuotes);
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while retrieving selected insurance quotes.',
      });
    }
  }

  *getAvailableQuotesByLoanId() {
    try {
      const response = yield this.insuranceInformationService.getAvailableQuotesByLoanId(this.loanId);
      this.setQuotes(JSON.parse(response?.data?.data));
      this.createOptionQuotes(this.quotes)
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while retrieving insurance quote details.',
      });
    }
  }

  *getPropertiesMissingQuotes() {
    try {
      const response = yield this.insuranceInformationService.getPropertiesMissingQuotes(this.loanId);
      this.propertiesMissingQuotes = JSON.parse(response?.data?.data) ?? [];
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while retrieving properties with quote details.',
      });
    }
  }

  *uploadInsuranceQuoteDocuments(documents) {
    try {
      const formData = new FormData();
      const taskId = this.taskId;
      formData.append('taskId', taskId.toString());
      formData.append('insuranceQuoteDetailId', this.currentQuote?.insuranceQuoteDetailId);
      documents.forEach(document => {
        const blob = document?.file.slice(0, document?.file.size, document?.file.type);
        const newFile = new File([blob], document?.name, { type: document?.file.type });
        formData.append('file', newFile);
      });
      const response = yield this.insuranceInformationService.uploadInsuranceQuoteDocuments(formData);
      this.getAvailableQuotesByLoanId();
      this.getSelectedQuotesByLoanId();
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while uploading the document(s).',
      });
    }
  }

  *saveInsuranceQuoteDetails(quote) {
    try {
      const quoteJson = {
        ...this.currentQuote,
        ...quote,
        loanId: this.loanId,
      };
      const data = new Blob([JSON.stringify(quoteJson)], { type: 'application/json' });
      const formData = new FormData();
      formData.append('taskId', this.taskId.toString());
      formData.append('data', data);
      if (this.isSaveInsuranceQuoteActive) {
        quote?.documents?.forEach(document => {
          const blob = document?.file.slice(0, document?.file.size, document?.file.type);
          const newFile = new File([blob], document?.name, { type: document?.file.type });
          formData.append('file', newFile);
        });
      }
      const response = yield this.insuranceInformationService.saveInsuranceQuoteDetail(formData);
      this.getAvailableQuotesByLoanId();
      this.getSelectedQuotesByLoanId();
      this.getPropertiesMissingQuotes();
      this.handleCloseSave();
    } catch (e) {
      const { error } = e;
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: error?.response?.data?.error?.message || GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *downloadInsuranceQuoteDocument(currentDocument) {
    try {
      const blob = yield this.insuranceInformationService.downloadInsuranceQuoteDocument(currentDocument?.insuranceQuoteDocumentId);
      const reader = new FileReader();
      reader.readAsArrayBuffer(blob);
      reader.onloadend = () => {
        if (currentDocument?.name.includes('pdf')) {
          // Preview the PDF
          this.pdfTitle = currentDocument?.name;
          this.pdfData = reader.result; // The ArrayBuffer
          this.isPdfPreviewModalOpen = true;
        } else {
          // Download the file if not a PDF
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = currentDocument?.name;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      };
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while downloading the document.',
      });
    }
  }

  *getLoanDetails(loanId: string) {
    try {
      const response: ApiResponse = yield this.insuranceInformationService.getLoanDetails(loanId);
      this.loanDetails = response.data.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      });
    }
  }

  getPropertiesOptions() {
    this.propertiesOptions = this.loanDetails?.properties?.map(property => ({
      label: property?.address,
      value: property?.propertyId,
    })) ?? [];
    if (this.propertiesOptions.length > 1) {
      this.propertiesOptions.unshift({ label: 'All Properties', value: 'All' });
      this.insuranceQuoteFormStore.onFieldChange('properties', ['All']);
      return;
    }
    this.insuranceQuoteFormStore.onFieldChange('properties', this.propertiesOptions.map(o => o.value));
  }

  validateQuote(quote: any) {
    this.insuranceQuoteFormStore.runFormValidation();
    this.insuranceQuoteFormStore.setDocumentIsvalid(quote?.documents.length > 0);
    if ((quote?.documents.length == 0 && !quote?.insuranceQuoteDetailId) || !this.insuranceQuoteFormStore.form.meta.isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please fill out the required fields in the quote',
      });
      return false;
    }
    return true;
  }

  *requestQuoteFromElmsure() {
    try {
      yield this.insuranceInformationService.requestElmsureQuote(this.loanId, this.insuranceQuoteFormStore.getRequestElmsureQuoteValues());
      this.getAvailableQuotesByLoanId();
      this.getSelectedQuotesByLoanId();
      this.getPropertiesMissingQuotes();
      this.insuranceQuoteFormStore.reset();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Elmsure quote requested successfully',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error has occurred requesting a quote from elmsure',
      });
    }
  }

  *deleteQuoteDocument(quoteDocumentId: number) {
    try {
      yield this.insuranceInformationService.deleteQuoteDocument(quoteDocumentId);
      yield this.getAvailableQuotesByLoanId();
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error deleting quote document',
      });
    }
  }

  deleteQuote(quoteId: number) { }
  finalApproveInsurance() { }
  approveInsurance() { }
  revertInsurance() { }

  *resendElmsureQuote() {
    try {
      if (this.resendElmsureQuoteStore.runFormValidationWithMessage()) {
        const response = yield this.insuranceInformationService.resendQuote(this.loanId, this.resendElmsureQuoteStore.getValue('emailAddress'));
        let success = false;
        let responseMessage;
        try {
          const parsed = JSON.parse(response?.data?.data);
          success = !!parsed?.success;
          responseMessage = parsed?.responseMessage;
        } catch (parseError) {
          success = false;
        }
        if (success) {
          this.resendElmsureQuoteModal = false;
          this.globalStore.notificationStore.showSuccessNotification({
            message: 'Elmsure quote has been resent to the borrower'
          });
        } else {
          console.error('resendElmsureQuote failed for loanId', this.loanId, responseMessage || response?.data?.data);
          this.globalStore.notificationStore.showErrorNotification({
            message: 'Error resending the elmsure quote to the borrower. Please contact the Elmsure team.',
          });
        }
      }
    } catch (e) {
      console.error('resendElmsureQuote error for loanId', this.loanId, e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error resending the elmsure quote to the borrower. Please contact the Elmsure team.',
      });
    }
  }

  *refreshQuotes() {
    try {
      let loanIds = [this.loanId];
      yield this.insuranceInformationService.refreshQuotes({ loanIds: loanIds, skipUpdateAcceptedInsuranceValidation: true })
    } catch (error) {
      console.error(error)
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error ocurred while refreshing quotes',
      });
    }
  }

  *getFinalDocuments() {
    try {
      return yield this.insuranceInformationService.getFinalDocuments(this.loanId);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error getting final documents',
      });
    }
  }

  *createFinalDocument(type: FinalDocumentType, insuranceQuoteDocumentIds: number[]) {
    try {
      return yield this.insuranceInformationService.createFinalDocument(this.taskId, type, insuranceQuoteDocumentIds);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error creating final document',
      });
    }
  }

  *downloadFinalDocument(type: FinalDocumentType) {
    try {
      const filesDictionary = {
        [FinalDocumentType.BINDER]: 'Final Binder',
        [FinalDocumentType.INVOICE]: 'Final Invoice',
        [FinalDocumentType.FLOOD]: 'Flood Certificate',
        [FinalDocumentType.ELMSURE_BINDER]: 'Final Elmsure Binder',
        [FinalDocumentType.ELMSURE_INVOICE]: 'Final Elmsure Invoice',
        [FinalDocumentType.ELMSURE_PAID_RECEIPT]: 'Elmsure Paid Receipt',
      };
      const blob = yield this.insuranceInformationService.downloadFinalDocument(
        type,
        this.loanId
      );
      const reader = new FileReader();
      reader.readAsArrayBuffer(blob);
      reader.onloadend = () => {
        this.pdfTitle = filesDictionary[type] + ".pdf";
        this.pdfData = reader.result; // The ArrayBuffer
        this.isPdfPreviewModalOpen = true;
      };
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while downloading the document.',
      });
    }
  }

  *getInsuranceGuidelinesData() {
    try {
      const response: ApiResponse = yield this.insuranceInformationService.getInsuranceGuidelinesData(this.loanId);
      this.guidelinesData = JSON.parse(response?.data?.data);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      });
    }
  }

  *downloadUnclassifiedDocument(loanId: number, documentId: number, documentName: string) {
    try {
      const blob = yield this.insuranceInformationService.downloadUnclassifiedDocument(loanId, documentId);
      const reader = new FileReader();
      reader.readAsArrayBuffer(blob);
      reader.onloadend = () => {
        if (documentName.includes('pdf')) {
          this.pdfTitle = documentName;
          this.pdfData = reader.result;
          this.isPdfPreviewModalOpen = true;
        } else {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = documentName;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      };
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while downloading the document.',
      });
    }
  }

  *getUnclassifiedDocuments() {
    try {
      return yield this.insuranceInformationService.getUnclassifiedDocuments(this.loanId);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error getting unclassified documents',
      });
    }
  }

  *deleteUnclassifiedDocument(documentId: number) {
    try {
      yield this.insuranceInformationService.deleteUnclassifiedDocument(documentId);
      this.loadUnclassifiedDocuments();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error deletting unclassified document',
      });
    }
  }

  *classifiedUnclassifiedDocument(documentId: number, insuranceQuoteDetailId: number) {
    try {
      yield this.insuranceInformationService.classifiedUnclassifiedDocument(documentId, insuranceQuoteDetailId);
      this.loadUnclassifiedDocuments();
      yield this.getAvailableQuotesByLoanId();
      yield this.getSelectedQuotesByLoanId();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error classifying document',
      });
    }
  }

  *generateInvoiceDocument() {
    this.globalStore.notificationStore.showWarningNotification({
      message: 'Method not allowed',
    });
  }

}
